import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { EmptyState } from '@ndrc/ui';
import { t } from '../messages';

/**
 * Une app qui plante ne casse pas le bureau.
 *
 * Sans cette frontière, une exception levée dans le rendu d'une app démonte tout l'arbre React :
 * l'élève perd la barre de menu, le dock, et jusqu'au moyen de fermer la fenêtre — écran blanc,
 * en pleine séance. Ici, la fenêtre reste, avec un message qui dit quoi faire.
 *
 * Le message d'erreur technique n'est **pas** affiché : il est écrit dans la console pour le
 * développeur. Une pile d'appels devant une classe n'aide personne.
 */
interface Props {
  /** Change quand on ouvre une autre app : remet la frontière à zéro. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Seul journal disponible côté navigateur ; jamais affiché à l'écran.
    console.error("NDRC Labs — une application s'est arrêtée :", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <EmptyState title={t('host.crashedTitle')} hint={t('host.crashedHint')}>
        <button
          type="button"
          className="ndrc-button"
          onClick={() => {
            this.setState({ failed: false });
          }}
        >
          {t('host.crashedReload')}
        </button>
      </EmptyState>
    );
  }
}
